import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DebugLogger from "../../../helpers/DebugLogger";
import axios from "../../../api/axios";

import "./VideoUploader.scss";
import { IconJsxer } from '../../../helpers/IconHelper';
import ModuleTextInput from '../../v2/CustomisationWorkspace/WorkspaceComponents/ModuleList/components/ModuleTextInput';
import { convertUniqueFilenameToOriginal } from '../../v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions';

import AudioIcon from '../../v2/CustomisationWorkspace/WorkspaceAssets/ModuleIcons/Audio.png';
import WaveSurfer from 'wavesurfer.js';

export const UPLOADER_PREVIEW_SIDE = {left: 'left', top: 'top'};

/**
 * Video uploader component.
 * Select a video to upload and see an immediate preview.
 * Optionally upload the video immediately or return the selected file to a callback so it can be uploaded as part of a larger form.
 * 
 * PROPS (mostly self explnatory, but some may need a little extra explanation, all are optional and have defaults / warnings if recommended):
 * 
 * label = A label for your uploader
 * uploadImmediately = true / false. If true then the uploader will attempt to upload your video to a provided endPoint as soon as it is selected.
 * endPoint = This is your endpoint for immediate uploads, for profile pics this is uploadProfilePicRoute from api/routes.js
 * removeEndPoint = Only really needed for immediate uploads, an endpoint to call to remove video files from the server / db, see removeProfilePicRoute in api/routes.js
 * id = An id for your video, when set to uploadImmediately the id will be passed to the endPoint otherwise it is passed to the callbacks detailed below
 * extraData = Optional extra data passed to your endpoint. If this is an object then each property is extracted and passed up alongside your id
 * 
 * -- Only used when uploadImmediately=false --
 * fileSelectedCallback = A callback that receives and object in this format: {id: id, file: file, originalEvent: e}, 
 *                        this is a different signature to the other callbacks simply because you will most likely need all the data that gets passed back
 *                        (you will probably need to store the selected audio data somewhere ready for submitting to your endpoint),
 *                        it fires when a file is selected just like it would if you used <input type="file"> directly instead of this component but with
 *                        a little bit more data included.
 *                        When removing the audio you will receive: {id: id, file: null, originalEvent: 'removed'}, allowing you to remove the audio from your stored form data.
 * 
 * -- Only used when uploadImmediately=true --
 * uploadedCallback = A callback that receives (res, file, id), you can probably ignore most params, but When uploading profile pics, I use it to set the profile pic
 *                      in authcontext userData.profilePic = res.data.uploadedFilename; so we can immediately start showing the new profile pic without asking the api for it.
 * 
 * -- Only used when uploadImmediately=true --
 * uploadErrorCallback = A callback you can use to deal with upload errors, but you probably won't need it...
 * 
 * -- Only used when uploadImmediately=true, if false you should use a fileSelected callback instead --
 * removedCallback = A callback you can use to detect when an audio is removed.
 *                      I use it when a user removes their profile audio to set the profile audio data on authcontext as with uploadedCallback
 * 
 * Hopefully that's all clear! :) - Carl
 * 
 * @param {*} props 
 * @returns The component
 */
const VideoUploaderV2 = (props) => {
  const { label, labelNote, showRemoveOption, uploadImmediately, endPoint, removeEndPoint, id, extraData, fileSelectedCallback, uploadedCallback, uploadErrorCallback, removedCallback } = props;
  let currentFile = props.currentFile;
  let currentFileName = props.currentFileName;
  let fileData = props.fileData;

  // Warn about missing props...
  if (uploadImmediately && endPoint === '') {
    DebugLogger.error('No endPoint provided to Video Uploader, you should provide one if uploadImmediately=true.');
  }
  if (!uploadImmediately && fileSelectedCallback === null) {
    DebugLogger.warn('No fileSelectedCallback provided to Video Uploader, you should provide one if uploadImmediately=false.');
  }
  if (showRemoveOption && uploadImmediately && removeEndPoint === '') {
    DebugLogger.error('No removeEndPoint provided to Video Uploader, you should provide one if uploadImmediately=true and showRemoveOption=true.');
  }
  if (showRemoveOption && uploadImmediately && removedCallback === null) {
    DebugLogger.warn('You should provide a removedCallback when using uploadImmediately=true and showRemoveOption=true.');
  }

  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(currentFile || null);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(currentFileName || null);
  const fileInputRef = useRef(null);
  const videoPlayerRef = useRef(null);

  useEffect(
    () => {
      setFile(null);
      setPreviewSrc(null);
      setTimeout(
        () => setPreviewSrc(props.currentFile),
        100
      );

      setFileName(props.currentFileName);
    },
    [props.currentFile, props.currentFileName]
  )

  const chooseFile = () => {if (!busy) fileInputRef.current.click();};
  
  const handleFile = (e) => {
    e.preventDefault();
    if (!busy) {
      if (e.target.files.length === 0) {
        return;
      }
      const file = e.target.files[0];
      setPreviewSrc(null);
      setTimeout(
        () => setPreviewSrc(URL.createObjectURL(file)),
        100
      );
      if (uploadImmediately) {
        DebugLogger.log('Attempting to upload video: ', file);
        if (endPoint) {
          const formData = new FormData();
          formData.append('video', file);
          if (id) {
            formData.append('id', id);
          } else {
            DebugLogger.warn('No id provided for video upload.')
          }
          if (extraData) {
            if (typeof extraData === 'string' || typeof extraData === 'number' || typeof extraData === 'boolean') {
              formData.append('extraData', extraData);
            } else {
              for (let prop in extraData) {
                formData.append(prop, extraData[prop]);
              }
            }
          }
          setBusy(true);

          axios.post(endPoint, formData, { withCredentials: true })
            .then(res => {
              DebugLogger.log('Upload result: ', res);
              setBusy(false);
              if (uploadedCallback) {
                uploadedCallback(res, file, id);
              }
            })
            .catch(err => {
              DebugLogger.log('Upload error: ', err);
              setBusy(false);
              if (uploadErrorCallback) {
                uploadErrorCallback(err, id);
              }
            });
        } else {
          DebugLogger.error('Could not upload video, no endPoint provided.');
        }
      } else {
        if (fileSelectedCallback) {
          fileSelectedCallback({id: id, file: file, originalEvent: e});
        }
      }
    }
  }

  const handleRemove = (e) => {
    e.preventDefault();
    if (!busy) {
      if (uploadImmediately) {
        DebugLogger.log('Attempting to remove video...');
        if (removeEndPoint) {
          const formData = new FormData();
          if (id) {
            formData.append('id', id);
          } else {
            DebugLogger.warn('No id provided for video removal.')
          }
          if (extraData) {
            if (typeof extraData === 'string' || typeof extraData === 'number' || typeof extraData === 'boolean') {
              formData.append('extraData', extraData);
            } else {
              for (let prop in extraData) {
                formData.append(prop, extraData[prop]);
              }
            }
          }
          setBusy(true);

          axios.post(removeEndPoint, formData)
            .then(res => {
              DebugLogger.log('Remove video result: ', res);
              setBusy(false);
              fileInputRef.current.value = '';
              setPreviewSrc(null);
              currentFile = null;
              if (removedCallback) {
                removedCallback(id);
              }
            })
            .catch(err => {
              DebugLogger.log('Remove video error: ', err);
              setBusy(false);
            });
        } else {
          DebugLogger.error('Could not remove video, no removeEndPoint provided.');
        }
      } else {
        fileInputRef.current.value = '';
        setPreviewSrc(null);
        currentFile = null;
        if (fileSelectedCallback) {
          fileSelectedCallback({id: id, file: null, originalEvent: 'removed'});
        }
        if (removedCallback) {
          removedCallback(id);
        }
      }
    }
  }

  return (
    <div className='v2-basic-module-holder'>
      <div className="v2-module-columns-holder">
        <div className='fl-column'>

          <div className='fl-column compact'>
              <ModuleTextInput
                  fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: 'transparent', backgroundImage: `url(${AudioIcon})`, backgroundSize: '1.8rem', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', borderWidth: 0}} />}
                  value={typeof fileData === 'string' ? fileData : convertUniqueFilenameToOriginal(fileData?.originalFile) ?? '<No Audio File>'}
                  fieldId={'audio-file'}
                  enabled={props.isEnabled}
                  editable={false}
                  onChange={null}
                  squareBottom={false}
              />
          
              <div className='fl-row compact'>
                <div className={`video-preview-holder big`}>
                    {previewSrc &&
                        <video width={320} height={179} controls>
                            <source src={previewSrc} />
                        </video>
                    }
                </div>
              </div>
          </div>
          <div className={`pill-button${busy ? ' disabled' : ''}`} onClick={busy ? null : chooseFile}>{busy ? 'Uploading' : 'Upload file (.mp4, .webm only)'}</div>
              <input
                type="file"
                accept=".mp4, .webm"
                name="video"
                onChange={handleFile}
                hidden
                ref={fileInputRef}
                id={id}
              />
        </div>
      </div>
    </div>
  );
}

VideoUploaderV2.propTypes = {
  label: PropTypes.string,
  currentFile: PropTypes.any,
  currentFileName: PropTypes.string,
  endPoint: PropTypes.string,
  removeEndPoint: PropTypes.string,
  showRemoveOption: PropTypes.bool,
  id: PropTypes.string,
  extraData: PropTypes.any,
  uploadImmediately: PropTypes.bool,
  fileSelectedCallback: PropTypes.func,
  uploadedCallback: PropTypes.func,
  uploadErrorCallback: PropTypes.func,
  removedCallback: PropTypes.func,
};

VideoUploaderV2.defaultProps = {
  label: 'Custom Video',
  currentFile: '',
  currentFileName: 'Current File',
  endPoint: '',
  removeEndPoint: '',
  showRemoveOption: true,
  id: '',
  extraData: null,
  uploadImmediately: false,
  fileSelectedCallback: null,
  uploadedCallback: null,
  uploadErrorCallback: null,
  removedCallback: null,
}

export default VideoUploaderV2; 
